import { createFormData } from 'hooks';
import i18n from 'i18nextInstance';
import moment from 'moment';
import { professionalActions } from './';
import professionalServices from './services';
import {
    push
} from 'react-router-redux';
import {
    call,
    put,
    select,
    takeEvery
} from 'redux-saga/effects';

const parseProfessionals = list => list.map(({
    birth_date,
    city = {},
    created_at,
    district = {},
    expiration_date,
    province = {},
    specialties = [],
    ...professional
}) => ({
    ...professional,
    birth_date: moment(birth_date).format('DD/MM/YYYY'),
    city: city?.name ?? '',
    created_at: moment(created_at).format('DD/MM/YYYY HH:mm'),
    district: district?.name ?? '',
    expiration_date: !!expiration_date ? moment(expiration_date).format('DD/MM/YYYY') : '',
    expired: !!expiration_date && moment(expiration_date).isBefore(moment(), 'day'),
    province: province?.name ?? '',
    specialties: specialties.map(({ name }) => name).join(' - ')
}));

function* _createProfessionalExternalLogin ({ payload }) {
    try {
        yield put(professionalActions.setProfessionalLoading(true));
        const { data: { redirect } } = yield call(professionalServices.postProfessionalExternalLogin, null, payload);
        yield put(professionalActions.setProfessionalExternalLogin(redirect));
    } catch (e) {
        const message = e?.response?.data?.message;
        if (message === 'PROFESSIONAL_DOES_NOT_EXIST') {
            yield put(professionalActions.setProfessionalExternalLoginError(i18n.t('El profesional no existe en la plataforma')));
        }
    } finally {
        yield put(professionalActions.setProfessionalLoading(false));
    }
}

function* _deleteProfessional ({ payload }) {
    try {
        yield call(professionalServices.removeProfessional, { doctor_id: payload });
        yield put(push(i18n.t('/profesionales/listar')));
    } catch (e) {}

}
function* _getProfessionalListing ({ payload }) {
    try {
        yield put(professionalActions.setProfessionalLoading(true));
        const { data } = yield call(professionalServices.getProfessionalsList, null, payload);
        yield put(professionalActions.setProfessionalList(parseProfessionals(data)));
    } catch (e) {

    } finally {
        yield put(professionalActions.setProfessionalLoading(false));
    }
}

function* _getProfessional ({ payload }) {
    try {
        yield put(professionalActions.setProfessionalLoading(true));
        const { data } = yield call(professionalServices.fetchProfessional, { id: payload });
        yield put(professionalActions.setProfessionalSelected(data));
    } catch (e) {

    } finally {
        yield put(professionalActions.setProfessionalLoading(false));
    }
}

function* _professionalSetEnabled ({ payload, payload: { enabled, doctor_id } }) {
    try {
        yield put(professionalActions.setProfessionalBusy(true));
        yield call(professionalServices.setEnabled, null, payload);
        const { professional: { list } } = yield select();
        const item = list.find(({ id }) => id === doctor_id);
        if (item) {
            const newElem = { ...item, enabled };
            const newList = [...list.filter(({ id }) => id !== doctor_id), newElem];
            yield put(professionalActions.setProfessionalList(newList));
        }
    } catch (e) {

    } finally {
        yield put(professionalActions.setProfessionalBusy(false));
    }
}

function* _saveProfessional ({ payload: { data, doctorId, file, mode, sign_image, stamp_image } }) {
    try {
        yield put(professionalActions.setProfessionalSaveLoading(true));
        const editMode = mode === i18n.t('editar');
        const { data: { doctor_id, user_id } } = !editMode
            ? yield call(professionalServices.postProfessional, null, data)
            : yield call(professionalServices.putProfessional, { doctor_id: doctorId }, data);
        if (file) {
            yield call(professionalServices.uploadAvatar, null, createFormData({
                avatar: file,
                ...(user_id && { user_id })
            }));
        }

        if (sign_image || stamp_image) {
            yield call(professionalServices.uploadSignAndStamp, { doctor_id }, createFormData({
                ...(sign_image && { sign_image }),
                ...(stamp_image && { stamp_image })
            }));
        }

        yield put(professionalActions.setProfessionalSelected({}));
        yield put(push('/profesionales/listar'));
    } catch (e) {
        const errors = e?.response?.data;
        if (!!errors) {
            yield put(professionalActions.setProfessionalRegistrationErrorModal(true));
            yield put(professionalActions.setProfessionalRegistrationError({
                ...(errors.identification && errors.identification[0]  === 'IDENTIFICATION_ALREADY_IN_USE'
                    && { identification: i18n.t('Ya fue utilizado') }),
                ...(errors.email && errors.email[0]  === 'EMAIL_ALREADY_IN_USE'
                    && { email: i18n.t('Ya fue utilizado') })
            }));
        }
    } finally {
        yield put(professionalActions.setProfessionalSaveLoading(false));
    }
}

function* _sendProfessionalNotification ({ payload: { doctor_id } }) {
    try {
        const { data } = yield call(professionalServices.notifyProfessional, { doctor_id });
        switch (data) {
        case ('DOCTOR_BUSY'): {
            yield put(professionalActions.setProfessionalNotificationResponse(i18n.t('La notificación no fue enviada debido a que el profesional se encuentra ocupado.')));
            break;
        }
        case ('DOCTOR_UNAVAILABLE'): {
            yield put(professionalActions.setProfessionalNotificationResponse(i18n.t('La notificación no fue enviada debido a que el profesional no se encuentra disponible.')));
            break;
        }
        case ('NOTIFICATION_SENT'): {
            yield put(professionalActions.setProfessionalNotificationResponse(i18n.t('La notificación fue enviada correctamente y el profesional debería recibir un aviso en su perfil. Espera a recibir su confirmación.')));
            break;
        }
        default: {
            yield put(professionalActions.setProfessionalNotificationResponse(`${ i18n.t('Es posible que la notificación no se haya enviado correctamente. Por favor, informe este caso con el código') }: ${ data }`));
            break;
        }
        }
    } catch (e) {

    }
}

export default [
    takeEvery(professionalActions.createProfessionalExternalLogin.type, _createProfessionalExternalLogin),
    takeEvery(professionalActions.deleteProfessional.type, _deleteProfessional),
    takeEvery(professionalActions.getProfessionalListing.type, _getProfessionalListing),
    takeEvery(professionalActions.getProfessional.type, _getProfessional),
    takeEvery(professionalActions.professionalSetEnabled.type, _professionalSetEnabled),
    takeEvery(professionalActions.saveProfessional.type, _saveProfessional),
    takeEvery(professionalActions.sendProfessionalNotification.type, _sendProfessionalNotification)
];
